import React from "react";
import {
  TrendingDown, DollarSign, PackageX, ArrowRight, Check,
} from "lucide-react";

import SectionLabel from "./SectionLabel";

/**
 * Problem — three real pains tied to real AIGO fixes.
 * Left: pain (rose accent). Right: AIGO fix (emerald accent).
 */
const pains = [
  {
    icon: <DollarSign className="w-4 h-4" />,
    pain: {
      title: "See your true margin, with COGS built in",
      body: "Shopify shows revenue. It doesn't show contribution margin. You're profitable on paper — bleeding in reality.",
    },
    fix: {
      title: "Contribution-Margin P&L + Profit Waterfall",
      body: "AIGO pulls unit economics from your live store and shows exactly where every dollar leaks — refunds, shipping, ad spend, discounts.",
    },
  },
  {
    icon: <TrendingDown className="w-4 h-4" />,
    pain: {
      title: "Keep every ad dollar working at its best",
      body: "Meta and Google keep spending on losing keywords, dead audiences, and stale creatives — you don't have the time to catch it.",
    },
    fix: {
      title: "$-Leak radar + Untapped-opportunity capture",
      body: "AIGO surfaces the top 3 leaks daily (e.g. \"ROAS down 12% on Google Ads — bleed keyword found\") with the exact $/mo impact and a one-click Apply Negative Fix.",
    },
  },
  {
    icon: <PackageX className="w-4 h-4" />,
    pain: {
      title: "Free up cash from slow stock to reinvest in winners",
      body: "Inventory sits. Ads still push to it. Meanwhile hero SKUs run out of stock. The cash cycle grinds.",
    },
    fix: {
      title: "Inventory Guard + 1-click markdowns",
      body: "AIGO recommends slow-mover markdowns with the exact $/mo impact and re-routes budget to hero SKUs — you approve in one tap.",
    },
  },
];

export const Problem = () => (
  <section
    id="problem"
    data-testid="problem-section"
    className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-14">
        <SectionLabel>The reality</SectionLabel>
        <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 max-w-4xl mx-auto mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
          Your data already holds the answers. <br className="hidden md:block" />
          <span className="text-emerald-600">We help you act on them.</span>
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Every leak below has a real AIGO fix. Free tier surfaces the leak <strong className="text-slate-900">and</strong> the $ impact. Pro implements it.
        </p>
      </div>

      <div className="grid gap-5 md:gap-6 max-w-5xl mx-auto">
        {pains.map((p, i) => (
          <div
            key={p.pain.title}
            data-testid={`problem-row-${i}`}
            className="grid md:grid-cols-2 gap-4 md:gap-6 rounded-3xl border border-slate-200 overflow-hidden bg-white"
          >
            {/* Pain side */}
            <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-slate-100 bg-slate-50/50">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-rose-600 mb-3">
                <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                  {p.icon}
                </span>
                Problem
              </div>
              <h3 className="text-slate-900 text-lg md:text-xl font-bold leading-tight tracking-tight">
                {p.pain.title}
              </h3>
              <p className="mt-2 text-slate-600 text-sm md:text-base leading-relaxed">
                {p.pain.body}
              </p>
            </div>
            {/* Fix side */}
            <div className="p-6 md:p-8">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700 mb-3">
                <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5" />
                </span>
                AIGO fix
              </div>
              <h3 className="text-slate-900 text-lg md:text-xl font-bold leading-tight tracking-tight">
                {p.fix.title}
              </h3>
              <p className="mt-2 text-slate-600 text-sm md:text-base leading-relaxed">
                {p.fix.body}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-700 font-bold">
                See it inside the free Growth Score <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Problem;
